import { FormBuilder } from '@/components/formBuilder';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import { ScorecardTemplateEditor } from '@/pages/ats-job-creation/tabs/interview-plan/stage/activity/activity-sections/schedule-interview/score-card/ScorecardTemplateEditor';
import { useSelector } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import { isEqual } from 'lodash-es';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { selectUsers } from '@/store/users/selectors';
import { ScorecardThresholdField } from '@/components/ats/scorecard-threshold-selector';
import { useKFlags } from '@/hooks/use-k-flags';
import type { MutableRefObject } from 'react';

const REVIEW_FORM_LABEL_STYLES = {
  fontSize: '14px',
  fontWeight: 'semiBold',
};

const getReviewInitialValue = (
  activity,
  allowedUserIds = [],
  isAdhoc = false,
  currentStageName
) => {
  const {
    participants = [],
    scorecard = {},
    scorecard_completion_threshold,
  } = activity || {};
  const label = isAdhoc
    ? `Review - ${currentStageName}`
    : activity?.name || `Review - ${currentStageName}`;

  return {
    label,
    name: label,
    participant_ids: participants
      // this filter will work only if the BE expose the can_view_candidate key
      ?.filter((item) => item?.can_view_candidate ?? true)
      // this filter is fallback check if the BE does not expose the can_view_candidate key
      ?.filter((item) => allowedUserIds?.includes(item?.id))
      ?.map((item) => item?.id),
    questions: scorecard?.questions,
    scorecard_completion_threshold: scorecard_completion_threshold ?? null,
  };
};

interface ReviewFormProps {
  formRef?: MutableRefObject<any> | null;
  scoreCardRef?: MutableRefObject<any> | null;
  handleSubmit?: (_values: any) => void | Promise<void>;
  activity?: any;
  application?: any;
  isAdhoc?: boolean;
}

export const ReviewForm = ({
  formRef,
  scoreCardRef,
  handleSubmit = () => {},
  activity = {} as any,
  application = {} as any,
  isAdhoc = false,
}: ReviewFormProps) => {
  const { feScorecardThreshold } = useKFlags();
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  const users = useSelector(selectUsers);
  let scorecardParams;
  const { department, office, employment_type } =
    currentJobData?.basic_info || {};
  if (currentJobData?.basic_info) {
    scorecardParams = {
      department_ids: [department.id],
      office_ids: office?.map((itr) => itr.id),
      employment_types: [employment_type],
    };
  }
  const [initialValues, setInitialValues] = useState({});

  const currentStageName =
    application?.ats_job_stages?.find(
      (s) => s?.id === application?.job_stage_id
    )?.name || '';

  const allowedUserIds = useMemo(() => users?.map((u) => u?.id), [users]);

  useEffect(() => {
    const val = getReviewInitialValue(
      activity,
      allowedUserIds,
      isAdhoc,
      currentStageName
    );
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInitialValues((prev) => (isEqual(prev, val) ? prev : val));
  }, [allowedUserIds, activity, isAdhoc, currentStageName]);

  const reviewFormConfig = useMemo(
    () => [
      {
        name: 'label',
        label: 'Review name',
        type: 'input',
        isRequired: true,
        placeholder: 'Add review name here',
        maxLength: 250,
        containerStyles: { width: '100%' },
        labelStyles: REVIEW_FORM_LABEL_STYLES,
      },
      {
        name: 'participant_ids',
        label: 'Add reviewers',
        type: 'autoComplete',
        options: users,
        isRequired: true,
        errorMessage: 'Atleast one reviewer is required',
        fetchConfig: {
          method: 'get',
          endPoint: `account/users/search?filter_by[status][]=active&filter_value=`,
          processResponse: (res) =>
            res?.data?.users?.map((item) => ({
              name: `${item?.first_name} ${item?.last_name}`,
              id: item?.id,
              avatar: item?.avatar_thumb_url,
              ...item,
            })),
          httpType: 'v1',
        },
        customFormatLabel: SelectOptionWithAvatar,
        components: {
          Option: SelectWithDescriptionOption,
        },
        extraProps: {
          showAvatar: true,
          labelKey: 'name',
          descKey: 'email',
          avatarKey: 'avatar',
          isNearLabel: true,
        },
        updateEndpointOnChange: true,
        loadDefaultOptions: true,
        useInput: true,
        isMulti: true,
        valueKey: 'id',
        labelKey: 'name',
        styleVersion: 'v1',
        placeholder: 'Select and add reviewers here',
      },
      ...(feScorecardThreshold
        ? [
            {
              name: 'scorecard_completion_threshold',
              type: 'custom',
              containerStyles: { width: '100%', mt: '16px', mb: '16px' },
              Field: (props) => (
                <ScorecardThresholdField
                  {...props}
                  reviewerIdsKey="participant_ids"
                />
              ),
            },
          ]
        : []),
    ],
    [feScorecardThreshold, users]
  );

  return (
    <>
      <FormBuilder
        formRef={formRef}
        config={reviewFormConfig}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      />
      <ScorecardTemplateEditor
        ref={scoreCardRef}
        wrapperProps={{ p: '0px' }}
        questions={activity?.scorecard?.questions || []}
        note="This is the recommended scorecard template. You can customize it but changes will only apply to this review."
        defaultScorecardParams={scorecardParams}
        scorecardData={{
          ...activity?.scorecard,
          is_feedback_mandatory:
            activity?.scorecard_config?.is_feedback_mandatory ??
            activity?.scorecard?.is_feedback_mandatory ??
            true,
        }}
        key={JSON.stringify(initialValues)}
      />
    </>
  );
};
